import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import i18n, { AVAILABLE_LANGUAGES, DEFAULT_LANGUAGE, FALLBACK_LANGUAGE, i18nReady } from '../../../i18n';
import useAuth from '../../../hooks/useAuth';
import { detectEnvironmentLanguage, loadStoredLanguage } from '../../../utils/language';

const LOCAL_STORAGE_KEY = 'loopino.language';

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  availableLanguages: AVAILABLE_LANGUAGES,
  setLanguage: () => {},
  setLanguageFromAuth: () => {},
  refreshUserLanguage: () => Promise.resolve(),
  isUpdating: false,
  lastError: null
});

const getNormalizedLanguage = (candidate) => {
  if (!candidate || typeof candidate !== 'string') {
    return DEFAULT_LANGUAGE;
  }

  const normalized = candidate.toLowerCase().split('-')[0];
  const isSupported = AVAILABLE_LANGUAGES.some(({ code }) => code === normalized);

  if (isSupported) {
    return normalized;
  }

  // Prefer fallback rather than default when possible
  return AVAILABLE_LANGUAGES.some(({ code }) => code === FALLBACK_LANGUAGE)
    ? FALLBACK_LANGUAGE
    : DEFAULT_LANGUAGE;
};

const resolveInitialLanguage = (userLanguage) => {
  const storedLanguage = loadStoredLanguage(LOCAL_STORAGE_KEY);
  if (storedLanguage) {
    return storedLanguage;
  }

  if (userLanguage) {
    return getNormalizedLanguage(userLanguage);
  }

  const detected = detectEnvironmentLanguage();
  return getNormalizedLanguage(detected);
};

export const LanguageProvider = ({ children }) => {
  const {
    user,
    updateUserLanguage,
    refreshUserLanguage: refreshUserLanguageFromAuth,
    setLocalUserLanguage
  } = useAuth();
  const [isInitializing, setIsInitializing] = useState(true);
  const [language, setLanguageState] = useState(() => resolveInitialLanguage(user?.language));
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastError, setLastError] = useState(null);
  const pendingLanguageRef = useRef(null);

  // Ensure i18next is ready (only relevant in very fast render cases)
  useEffect(() => {
    let isMounted = true;

    i18nReady.finally(() => {
      if (!isMounted) return;
      setIsInitializing(false);
    });

    return () => {
      isMounted = false;
    };
  }, []);

  // Keep provider state in sync with auth user changes
  useEffect(() => {
    if (!user?.language) {
      return;
    }

    const normalized = getNormalizedLanguage(user.language);
    if (normalized !== language) {
      applyLanguage(normalized, { skipBackend: true, skipAuthSync: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.language]);

  const persistLanguage = useCallback((nextLanguage) => {
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, nextLanguage);
    } catch (error) {
      console.warn('[LanguageProvider] Unable to persist language in localStorage', error);
    }
  }, []);

  const syncWithI18n = useCallback((nextLanguage) => {
    if (i18n.language !== nextLanguage) {
      i18n.changeLanguage(nextLanguage);
    }
  }, []);

  const notifyAuthContext = useCallback((nextLanguage) => {
    if (typeof setLocalUserLanguage === 'function') {
      setLocalUserLanguage(nextLanguage);
    }
  }, [setLocalUserLanguage]);

  const applyLanguage = useCallback((nextLanguage, options = {}) => {
    const { skipBackend = false, skipAuthSync = false } = options;
    const normalized = getNormalizedLanguage(nextLanguage);

    setLanguageState(normalized);
    persistLanguage(normalized);
    syncWithI18n(normalized);

    if (!skipAuthSync) {
      notifyAuthContext(normalized);
    }

    if (!skipBackend && user) {
      queueBackendUpdate(normalized);
    } else if (!user) {
      pendingLanguageRef.current = null;
      setIsUpdating(false);
    }
  }, [persistLanguage, syncWithI18n, notifyAuthContext, user]);

  const queueBackendUpdate = useCallback((nextLanguage) => {
    pendingLanguageRef.current = nextLanguage;
    setIsUpdating(true);
  }, []);

  const flushBackendUpdate = useCallback(async () => {
    if (!pendingLanguageRef.current) {
      return;
    }

    const languageToPersist = pendingLanguageRef.current;
    pendingLanguageRef.current = null;

    if (typeof updateUserLanguage !== 'function') {
      setIsUpdating(false);
      return;
    }

    try {
      setLastError(null);
      await updateUserLanguage(languageToPersist);
    } catch (error) {
      setLastError(error);
      // Rollback to previous persisted value if backend fails
      const storedLanguage = localStorage.getItem(LOCAL_STORAGE_KEY) || DEFAULT_LANGUAGE;
      setLanguageState(getNormalizedLanguage(storedLanguage));
      syncWithI18n(storedLanguage);
    } finally {
      setIsUpdating(false);
    }
  }, [updateUserLanguage, syncWithI18n]);

  // Debounce backend updates to avoid flooding API
  useEffect(() => {
    if (!isUpdating) {
      return;
    }

    const timer = window.setTimeout(() => {
      flushBackendUpdate();
    }, 400);

    return () => window.clearTimeout(timer);
  }, [isUpdating, flushBackendUpdate]);

  const handleLanguageChange = useCallback((nextLanguage) => {
    applyLanguage(nextLanguage);
  }, [applyLanguage]);

  const handleLanguageFromAuth = useCallback((nextLanguage) => {
    applyLanguage(nextLanguage, { skipBackend: true });
  }, [applyLanguage]);

  const refreshUserLanguage = useCallback(async () => {
    if (typeof refreshUserLanguageFromAuth !== 'function') {
      return;
    }

    try {
      const userLanguage = await refreshUserLanguageFromAuth();
      if (userLanguage) {
        applyLanguage(userLanguage, { skipBackend: true, skipAuthSync: true });
      }
    } catch (error) {
      console.warn('[LanguageProvider] Unable to refresh user language from backend', error);
    }
  }, [applyLanguage, refreshUserLanguageFromAuth]);

  const value = useMemo(() => ({
    language,
    availableLanguages: AVAILABLE_LANGUAGES,
    setLanguage: handleLanguageChange,
    setLanguageFromAuth: handleLanguageFromAuth,
    refreshUserLanguage,
    isReady: !isInitializing,
    isUpdating,
    lastError
  }), [language, handleLanguageChange, handleLanguageFromAuth, refreshUserLanguage, isInitializing, isUpdating, lastError]);

  useEffect(() => {
    persistLanguage(language);
  }, [language, persistLanguage]);

  useEffect(() => {
    syncWithI18n(language);
  }, [language, syncWithI18n]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = () => useContext(LanguageContext);

export default LanguageContext;
